import { useState, useEffect } from "react";
import "../Leads.css";
import { Undo2, X, Ban, Check } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { formatTime, formatUSPhone, getOwnerColor, getPhoneTypeLabel, makeFpsLink, parsePhone, statuses, updateLocalStorageRolling } from "../../../utils/common";
import { WRONG_PHONES as columns } from "../../../utils/column_maps";
import LoadingSpinner from "../../../components/LoadingSpinner";
import axiosInstance from "../../../api/axiosInstance";
import UndoLeadModal from "../../../components/UndoModal";
import { toast } from "react-toastify";

const WrongPhones = () => {
  const [formData, setFormData] = useState({
    filter: "no_working_number",
  });
  const [fetchLoading, setFetchLoading] = useState(true);
  const leadsPerPage = 25;
  const [totalLeads, setTotalLeads] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);

  const [selectedRow, setSelectedRow] = useState(null);
  const [undoModal, setUndoModal] = useState(false);

  const [visitedLinks, setVisitedLinks] = useState([]);

  const [leads, setLeads] = useState([]);

  const indexOfLastLead = currentPage * leadsPerPage;
  const indexOfFirstLead = indexOfLastLead - leadsPerPage;

  const totalPages = Math.ceil((totalLeads || 0) / leadsPerPage);

  const [selectedData, setSelectedData] = useState({});

  const [loadingBackupIds, setLoadingBackupIds] = useState({});

  const toggleOwner = (leadId, owner) => {
    setSelectedData((prev) => {
      const prevLeadId = Object.keys(prev)[0];

      if (prevLeadId && prevLeadId !== String(leadId)) {
        return {
          [leadId]: {
            owners: {
              [owner.id]: { owner, phones: [], email: null },
            },
          },
        };
      }

      const currentOwners = prev[leadId]?.owners || {};

      if (currentOwners[owner.id]) {
        const updatedOwners = { ...currentOwners };
        delete updatedOwners[owner.id];

        return Object.keys(updatedOwners).length ? { [leadId]: { owners: updatedOwners } } : {};
      }

      return {
        [leadId]: {
          owners: {
            ...currentOwners,
            [owner.id]: { owner, phones: [], email: null },
          },
        },
      };
    });
  };

  const togglePhone = (leadId, owner, phoneObj) => {
    setSelectedData((prev) => {
      const leadOwners = prev[leadId]?.owners || {};
      // Create a default owner entry if it doesn't exist
      const currentOwner = leadOwners[owner.id] || { phones: [], email: null };
      const exists = currentOwner.phones.some((p) => p.phone === phoneObj.phone);

      const phones = exists ? currentOwner.phones.filter((p) => p.phone !== phoneObj.phone) : [...currentOwner.phones, phoneObj];

      return {
        ...prev, // keep other leads
        [leadId]: {
          ...prev[leadId],
          owners: {
            ...leadOwners,
            [owner.id]: { ...currentOwner, phones },
          },
        },
      };
    });
  };

  const confirmSelection = ({ lead }) => {
    const selectedOwnersMap = selectedData[lead.id]?.owners || {};

    const owners = lead.owner
      .map((owner) => {
        const ownerId = owner.id;

        // const allPhones = lead.fpPhones?.filter((p) => p.owner_id === ownerId) || [];

        const selectedOwner = selectedOwnersMap[ownerId];

        if (selectedOwner) {
          const selectedPhones = selectedOwner.phones || [];

          // const unselectedPhones = allPhones.filter((p) => !selectedPhones.some((sp) => sp.phone === p.phone));

          return {
            firstName: owner.first_name || "",
            lastName: owner.last_name || "",
            email: selectedOwner.email || "",
            phones: selectedPhones.map((p) => `${p.phone}${p.type ? " " + getPhoneTypeLabel(p.type) : ""}`).join("\n"),
            // backupPhones: unselectedPhones.map((p) => `${p.phone}${p.type ? " " + getPhoneTypeLabel(p.type) : ""}`).join("\n"),
          };
        }

        // if (allPhones.length > 0) {
        // if (includeUnselectedOwners && allPhones.length > 0) {
        //   return {
        //     firstName: owner.first_name || "",
        //     lastName: owner.last_name || "",
        //     email: owner?.email || owner.email?.[0] || "",
        //     phones: "",
        //     // backupPhones: allPhones.map((p) => `${p.phone}${p.type ? " " + getPhoneTypeLabel(p.type) : ""}`).join("\n"),
        //   };
        // }

        return null;
      })
      .filter(Boolean);

    const selectedLead = {
      address: lead.full_address,
      owners,
      add_contacts: true,
    };

    const params = new URLSearchParams({
      lead: JSON.stringify(selectedLead),
      page: "1",
    });

    window.open(`/added-leads?${params.toString()}`, "_blank");
    setSelectedData({});
  };

  // const [includeUnselectedOwners, setIncludeUnselectedOwners] = useState(false);

  const getLeadFilters = (filter) => {
    switch (filter) {
      case "no_working_number":
        return {
          onlyWrongPhones: true,
        };

      case "no_contact_made":
        return {
          hasValidPhone: true,
        };

      case "contact_made":
        return {
          hasCorrectPhone: true,
        };

      default:
        return {};
    }
  };

  const fetchLeads = async (page = 1, limit = leadsPerPage, showLoader = true) => {
    try {
      if (showLoader) setFetchLoading(true);

      const newParams = new URLSearchParams(searchParams.toString());
      newParams.set("page", page);
      setSearchParams(newParams);

      const offset = (page - 1) * limit;

      const filterParams = getLeadFilters(formData.filter);
      if (filterParams.onlyWrongPhones) {
        delete filterParams.hasValidPhone;
        delete filterParams.hasCorrectPhone;
      } else if (filterParams.hasValidPhone) {
        delete filterParams.onlyWrongPhones;
        delete filterParams.hasCorrectPhone;
      } else if (filterParams.hasCorrectPhone) {
        delete filterParams.onlyWrongPhones;
        delete filterParams.hasValidPhone;
      }
      const resp = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL}api/dealmachine_properties/leads?ai_data=true`, {
        params: {
          limit,
          offset,
          owner: true,
          fpPhones: true,
          values: true,
          sortBy: "updated_at",
          sortOrder: "DESC",
          status: "contact_saved",
          ...filterParams,
        },
      });

      const data = await resp.data;

      if (data.success) {
        setLeads(data.leads || []);
        setTotalLeads(data.total);
        setCurrentPage(page);
      }
    } catch (err) {
      console.error("Failed to fetch leads", err);
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      fetchLeads(currentPage, leadsPerPage, false);
    }, 30000);

    return () => clearInterval(interval); // eslint-disable-next-line
  }, [currentPage, formData.filter]);

  useEffect(() => {
    setCurrentPage(1);
    fetchLeads(1, leadsPerPage, true); // eslint-disable-next-line
  }, [formData.filter]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [currentPage]);

  useEffect(() => {
    const storedVisited = JSON.parse(localStorage.getItem("visitedLinks") || "[]");
    setVisitedLinks(storedVisited);
  }, []);

  const totalTableWidth = columns.reduce((sum, col) => sum + col.width, 0);

  const handleUndo = async (id, complete = false) => {
    try {
      const res = await axiosInstance.delete(`api/dealmachine_properties/lead/${id}?complete=${complete}`);

      if (res.data.success) {
        console.log("Lead undo successful:", res.data);
        fetchLeads();
      } else {
        console.warn("Lead undo failed:", res.data.message);
      }
    } catch (error) {
      console.error("Error undo lead:", error.message || error);
    }
  };

  const mapLeadData = (lead) => ({
    id: lead.id || "Not Found",
    created_at: formatTime(lead.created_at),
    lead_submitted: formatTime(lead.lead_submitted),
    status: statuses[lead.status] || lead.status,

    full_address:
      (
        <div className="flex justify-between !space-x-4">
          <div className="flex item-center">
            <span>{lead.full_address}</span>
            <span className="!ml-2 flex !space-x-2">
              <a
                href={makeFpsLink(lead.address, lead.zip, lead.city, lead.state)}
                target="_blank"
                rel="noopener noreferrer"
                className={`!ml-2 text-nowrap ${visitedLinks.includes(lead.id) ? "!text-purple-800 text-medium" : "!text-blue-600 hover:!text-blue-800"}`}
                onClick={() => {
                  const updated = updateLocalStorageRolling("visitedLinks", lead.id);
                  setVisitedLinks(updated);
                }}
              >
                [ FPS ]
              </a>
            </span>
          </div>
        </div>
      ) || "Not Found",
    source_m: lead.source_m ? (
      <div className="whitespace-pre-line">
        {lead.source_m.name && <div>Name: {lead.source_m.name}</div>}
        {lead.source_m.phone && <div>Phone: {lead.source_m.phone}</div>}
        {lead.source_m.email && <div>Email: {lead.source_m.email}</div>}
      </div>
    ) : (
      ""
    ),
    mailing_address: lead?.propertyDetails?.owner_mailing_label || "Not Found",
    values: (
      <div className="flex flex-wrap gap-2">
        {lead.values?.length > 0 ? (
          lead.values.map((v) =>
            v.url ? (
              <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline text-sm">
                [{v.site}]
              </a>
            ) : null,
          )
        ) : (
          <span className="text-gray-400 text-sm"></span>
        )}
      </div>
    ),
    dm_lead_id: lead.dm_lead_id || "Not Found",
    owners: lead.owner?.length ? (
      <div>
        {lead.owner.map((o, idx) => (
          <div key={o.id || idx} className="font-semibold" style={{ color: getOwnerColor(idx) }}>
            {o.name}
          </div>
        ))}
      </div>
    ) : (
      "Not Found"
    ),
    fpPhones: lead.owner?.length ? (
      <>
        {lead.owner.map((owner, idx) => {
          const ownerPhones = lead.fpPhones?.filter((p) => p.owner_id === owner.id).map((p) => p) || [];
          const ownerEmails = [owner.email, owner.email_2, owner.email_3].filter(Boolean);
          const ownerSelected = !!selectedData[lead.id]?.owners?.[owner.id];

          return (
            <div key={idx} className="!mb-2 transition-all duration-200 ease-in-out">
              <label className="font-semibold flex item-center cursor-pointer" style={{ color: getOwnerColor(idx) }}>
                <input type="checkbox" className="!w-4 !h-4 accent-teal-600 !mr-2 cursor-pointer" checked={ownerSelected} onChange={() => toggleOwner(lead.id, owner)} />

                <span>{owner.name || "Unknown Owner"}</span>
              </label>

              {ownerPhones.map((phone, index) => {
                const isCorrect = phone.is_correct;
                const isDead = phone.is_dead;
                const isWrong = phone.is_wrong;
                const isDisabled = isCorrect || isDead || isWrong;

                let textColor = "text-gray-700";
                if (isCorrect) textColor = "text-green-700";
                else if (isDead || isWrong) textColor = "text-red-700";

                // Is the phone currently selected?
                const isChecked = selectedData[lead.id]?.owners?.[owner.id]?.phones?.some((p) => p.phone === phone.phone) ?? false;

                return (
                  <label key={index} className={`flex items-center pl-5 gap-2 ${isDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}>
                    <input type="checkbox" className={`!w-4 !h-4 accent-teal-600 ${isDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`} checked={isChecked && !isDisabled} onChange={() => togglePhone(lead.id, owner, phone)} disabled={isDisabled} />
                    <span className={textColor}>
                      {formatUSPhone(phone.phone)}
                      {phone.type && (
                        <>
                          {" - "}
                          {getPhoneTypeLabel(phone.type)}
                        </>
                      )}
                    </span>
                  </label>
                );
              })}

              {/* Show emails if needed */}
              {ownerEmails.length > 0 && <div className="ml-2 text-gray-500">{ownerEmails[0]}</div>}
            </div>
          );
        })}

        {!!selectedData[lead.id]?.owners && (
          <div className="!pl-10">
            <button onClick={() => confirmSelection({ lead })} className="!px-3 !py-1 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:bg-gray-400 cursor-pointer transition-all duration-200 ease-in-out">
              Add Contacts
            </button>
          </div>
        )}
      </>
    ) : (
      "Not Found"
    ),
    ghlSaved: lead.owner?.length
      ? lead.owner
          .filter((o) => o.added_ghl)
          .map((owner, idx) => {
            const ownerPhones = lead.fpPhones?.filter((p) => p.owner_id === owner.id && p.added_ghl).map((p) => p) || [];

            const ownerEmails = [owner.email, owner.email_2, owner.email_3].filter(Boolean);

            if (!ownerPhones.length && !ownerEmails.length) return null;

            return (
              <div key={idx} className="mb-2">
                <span className="font-semibold" style={{ color: getOwnerColor(idx) }}>
                  {owner.name || "Unknown Owner"}
                </span>

                {/* {ownerPhones.length > 0 && <div className="ml-2 text-gray-700 whitespace-pre-line">{ownerPhones.join("\n")}</div>} */}

                {ownerPhones.map((phone, index) => {
                  const isCorrect = phone.is_correct;
                  const isDead = !phone.is_correct && phone.is_dead;
                  const isWrong = !phone.is_correct && !phone.is_dead && phone.is_wrong;

                  let textColor = "text-gray-700";
                  if (isCorrect) textColor = "text-green-700";
                  else if (isDead || isWrong) textColor = "text-red-700";

                  return (
                    <div key={index} className={`ml-2 ${textColor} whitespace-pre-line`}>
                      <span className="flex justify-start items-center">
                        {formatUSPhone(phone.phone)}

                        {phone?.type && (
                          <>
                            {" - "}
                            {getPhoneTypeLabel(phone.type)}
                          </>
                        )}

                        {isCorrect ? (
                          <div className="flex items-center justify-center mx-1 space-x-1">
                            <span>-</span>
                            <Check size={20} />
                          </div>
                        ) : isDead ? (
                          <div className="flex items-center justify-center mx-1 space-x-1">
                            <span>-</span>
                            <Ban size={15} />
                          </div>
                        ) : isWrong ? (
                          <div className="flex items-center justify-center mx-1 space-x-1">
                            <span>-</span>
                            <X size={16} />
                          </div>
                        ) : null}
                      </span>
                    </div>
                  );
                })}

                {ownerEmails.length > 0 && <div className="ml-2 text-gray-500 whitespace-pre-line">{ownerEmails[0]}</div>}
              </div>
            );
          })
      : "Not Found",
    addAllBackups: (() => {
      const hasSelectablePhone = lead.fpPhones?.some((p) => !p.is_correct && !p.is_dead && !p.is_wrong);
      if (!hasSelectablePhone) return null;

      const isLoading = loadingBackupIds[lead.id] || false;

      return (
        <div>
          <button
            onClick={async () => {
              if (isLoading) return;
              const ownersMap = {};
              lead.owner?.forEach((owner) => {
                ownersMap[owner.id] = { owner, phones: [] };
              });

              lead.fpPhones?.forEach((phone) => {
                const isDisabled = phone.is_correct || phone.is_dead || phone.is_wrong;
                if (!isDisabled) {
                  const owner = lead.owner?.find((o) => o.id === phone.owner_id);
                  if (owner && ownersMap[owner.id]) {
                    ownersMap[owner.id].phones.push(phone);
                  }
                }
              });

              const ownersPayload = lead.owner
                ?.map((owner) => {
                  const selectedPhones = ownersMap[owner.id]?.phones || [];
                  if (selectedPhones.length === 0) return null;

                  const phoneStrings = selectedPhones.map((p) => `${p.phone}${p.type ? " " + getPhoneTypeLabel(p.type) : ""}`);
                  const parsedPhones = phoneStrings
                    .map(parsePhone)
                    .filter((p) => p?.phone)
                    .map((p) => ({ ...p, is_backup: false }));

                  return {
                    first_name: owner.first_name || "",
                    last_name: owner.last_name || "",
                    email: owner.email || "",
                    phones: parsedPhones,
                  };
                })
                .filter(Boolean);

              const payload = {
                full_address: lead.full_address,
                owners: ownersPayload,
                add_contacts: true,
              };

              setLoadingBackupIds((prev) => ({ ...prev, [lead.id]: true }));

              try {
                // console.log({ payload });
                const resp = await axiosInstance.post(`${process.env.REACT_APP_BASE_URL}api/dealmachine_properties/lead`, payload, { headers: { "Content-Type": "application/json" } });

                if (resp.data.success) {
                  toast.success("Backup phones added successfully!");
                  fetchLeads();
                } else {
                  toast.error(resp.data.message || "Failed to add backup phones.");
                }
              } catch (err) {
                console.error("Add all backup phones error:", err);
                toast.error("Failed to add backup phones. Please try again.");
              } finally {
                setLoadingBackupIds((prev) => ({ ...prev, [lead.id]: false }));
              }
            }}
            disabled={isLoading}
            className={`!px-3 !py-1 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:bg-gray-400 cursor-pointer transition-all duration-200 ease-in-out ${isLoading ? "opacity-70 cursor-wait" : ""}`}
          >
            {isLoading ? "Adding..." : "Add All Backup Phones"}
          </button>
        </div>
      );
    })(),
  });

  return (
    <>
      <div>
        <div className="input_fields_container">
          <form className="w-full grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <select
              className="input_field focus:!outline-none !w-full bg-white cursor-pointer "
              value={formData.filter}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  filter: e.target.value,
                }))
              }
            >
              <option value="no_working_number">no working number</option>
              <option value="no_contact_made">no contact made</option>
              <option value="contact_made">contact made</option>
            </select>
          </form>
        </div>

        <div className="mt-6 !w-full !overflow-x-auto">
          <table className="!border !border-gray-300 !table-auto min-w-[98%]" style={{ width: `${totalTableWidth}px` }}>
            <thead>
              <tr className="!bg-gray-200">
                <th className="!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap !w-8 !min-w-8"></th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`!border !border-gray-300 !px-4 !py-2`}
                    style={{
                      minWidth: `${col.width}px`,
                      width: `${col.width}px`,
                      textAlign: col.headerAlign,
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!fetchLoading ? (
                <>
                  {leads.length > 0 ? (
                    leads.map((lead, idx) => {
                      const row = mapLeadData(lead);
                      return (
                        <tr key={lead.id || idx} className="!hover:bg-gray-100">
                          <td className="!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap !w-4">
                            <button
                              onClick={() => {
                                setUndoModal(true);
                                setSelectedRow(lead);
                              }}
                              className="!text-red-600 hover:!text-red-800 !flex !items-center !justify-center !px-1 !py-2 cursor-pointer"
                              title="Undo Lead"
                            >
                              <Undo2 size={18} />
                            </button>
                          </td>
                          {columns.map((col) => (
                            <td
                              key={col.key}
                              className={`truncate !border !border-gray-300 !px-4 !py-2 text-sm
                            ${row[col.key] === "Not Found" ? "text-red-500" : /(CONFIRMED)/i.test((row[col.key] ?? "").toString()) ? "text-green-500" : /(NO-ANSWER)/i.test((row[col.key] ?? "").toString()) ? "text-yellow-500" : "text-black"}`}
                              style={{
                                minWidth: `${col.width}px`,
                                width: `${col.width}px`,
                                textAlign: col.cellAlign,
                                whiteSpace: "pre-line",
                              }}
                            >
                              {row[col.key]}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={columns.length} className="!border !border-gray-300 !p-4 !text-left !text-gray-500">
                        <div className="w-full max-w-screen text-center">No leads found.</div>
                      </td>
                    </tr>
                  )}
                </>
              ) : (
                <>
                  <tr>
                    <td colSpan={columns.length} className="!border !border-gray-300 !p-4 !text-left !text-gray-500">
                      <div className="w-full max-w-screen text-center">
                        <LoadingSpinner size={30} textSize={20} />
                      </div>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col items-center !mb-10">
          <span className="text-sm text-gray-700">
            Showing <span className="font-semibold text-gray-900">{indexOfFirstLead + 1}</span> to <span className="font-semibold text-gray-900">{Math.min(indexOfLastLead, totalLeads || 0)}</span> of <span className="font-semibold text-gray-900">{totalLeads || 0}</span> Entries
          </span>

          {/* Pagination */}
          <div className="inline-flex !mt-2 xs:mt-0">
            {/* Prev Button */}
            <button className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-s cursor-pointer bg-[#008080] hover:bg-[#009999] disabled:bg-[#006666]" onClick={() => fetchLeads(currentPage - 1)} disabled={currentPage === 1}>
              Prev
            </button>

            {/* First Page */}
            <button className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-none cursor-pointer ${currentPage === 1 ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => fetchLeads(1)} disabled={currentPage === 1}>
              1
            </button>

            {/* Ellipses before middle pages */}
            {currentPage > 3 && totalPages > 5 && <span className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-default bg-[#008080]">...</span>}

            {/* Middle Pages */}
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((page) => page !== 1 && page !== totalPages && page >= currentPage - 1 && page <= currentPage + 1)
              .map((page) => (
                <button key={page} className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-pointer ${currentPage === page ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => fetchLeads(page)}>
                  {page}
                </button>
              ))}

            {/* Ellipses after middle pages */}
            {currentPage < totalPages - 2 && totalPages > 5 && <span className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-default bg-[#008080]">...</span>}

            {/* Last Page */}
            {totalPages > 1 && (
              <button className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-none cursor-pointer ${currentPage === totalPages ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => fetchLeads(totalPages)} disabled={currentPage === totalPages}>
                {totalPages}
              </button>
            )}

            {/* Next Button */}
            <button className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-e cursor-pointer bg-[#008080] hover:bg-[#009999] disabled:bg-[#006666]" onClick={() => fetchLeads(currentPage + 1)} disabled={currentPage === totalPages}>
              Next
            </button>
          </div>
        </div>

        <UndoLeadModal
          isOpen={undoModal}
          title="Undo Lead"
          message="Do you want to undo this lead completely or only in Deal Machine?"
          onCancel={() => {
            setUndoModal(false);
            setSelectedRow(null);
          }}
          onCompleteUndo={() => handleUndo(selectedRow.id, true)}
          onDealMachineUndo={() => handleUndo(selectedRow.id, false)}
          buttonColor="red"
        />
      </div>
    </>
  );
};

export default WrongPhones;
